import type { GetAdminFirms } from '@cloudFunctions/types/admin/getAdminFirms';
import type { GetAdminMatters } from '@cloudFunctions/types/admin/getAdminMatters';
import type { MatterDocument } from '../../../../../shared/types/firestore/matter';
import type {
  TransactionDocument,
  TransactionData,
} from '../../../../../shared/types/firestore/transaction';
import { getAdminFirms, getAdminMatters } from '@/lib/cloudFunctions';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { firebase, shouldUseEmulators } from '@/lib/firebaseConfig';
import { firmKeys } from './queryKeys';
export type FirmInfo = GetAdminFirms.SuccessPayloadFirm;

// Lightweight matter type from Firestore (for table display)
export interface MatterInfo {
  id: string;
  name: string;
  description?: string;
  status: string;
  state: string;
  createdTimestamp: Date;
  matterType?: string;
  practiceArea?: string;
  responsibleAttorney?: string;
  trustBalance?: number;
}

// Full matter type with Finix payment instruments (for detail view)
export interface MatterDetailInfo {
  id: string;
  name: string;
  description?: string;
  status: string;
  state: string;
  createdTimestamp: Date;
  matterType?: string;
  practiceArea?: string;
  responsibleAttorney?: string;
  otherAssignee?: string;
  // Match the cloud function's `GetAdminMatters.SerializableMatterWithFinixData`
  // shape, which mirrors `MatterData` — these are stored as either string or
  // number in Firestore depending on when the matter was created.
  flatFeeAmt?: string | number;
  retainer?: string | number;
  responsibleAttyHourlyRate?: string;
  priorInvoicesBalance?: number;
  trustBalance?: number;
  source?: string;
  sourceId?: string;
  engagementOriginator?: string;
  referralOriginator?: string;
  leadSourceId?: string;
  scope?: string;
  primaryClientId?: string;
  primaryAdversePartyId?: string;
  defaultPaymentInstrumentId?: string;
  subStatusId?: string;
  viewedByResponsibleAttorney?: string;
  humansAndOrganizations?: Array<{
    id: string;
    type: 'human' | 'organization';
    role: 'client' | 'adverseParty';
  }>;
  organizationRepresentatives?: Record<string, string[]>;
  feeEarned?: Array<{
    id?: string;
    retainerTransferId?: string;
    amount: number;
    date: Date;
  }>;
  contingencyFees?: {
    pctPreFilingOfLawsuit?: number;
    pctUponFilingOfLawsuit?: number;
    pctUponCommencementOfTrial?: number;
  };
  _autoGeneratedFields?: {
    humanContactIds: string[];
    organizationContactIds: string[];
    clientContactIds: string[];
    adversePartyContactIds: string[];
  };
  finixIdentities?: GetAdminMatters.SerializableMatterWithFinixData['finixIdentities'];
}

// Helper to convert Firestore matter to lightweight client format
const convertFirestoreMatter = (doc: MatterDocument): MatterInfo => {
  return {
    id: doc.id,
    name: doc.name,
    description: doc.description,
    status: doc.status,
    state: doc.state,
    createdTimestamp:
      doc.createdTimestamp instanceof Date
        ? doc.createdTimestamp
        : new Date(doc.createdTimestamp),
    matterType: doc.matterType,
    practiceArea: doc.practiceArea,
    responsibleAttorney: doc.responsibleAttorney,
    trustBalance: doc.trustBalance,
  };
};

// Helper to convert serialized matter from cloud function to full detail format
const deserializeMatterDetail = (
  matter: GetAdminMatters.SerializableMatterWithFinixData,
): MatterDetailInfo => {
  return {
    ...matter,
    createdTimestamp: new Date(matter.createdTimestamp),
    feeEarned: matter.feeEarned?.map((fee) => ({
      id: fee.id,
      retainerTransferId: fee.retainerTransferId,
      amount: fee.amount,
      date: new Date(fee.date),
    })),
  };
};

export const useFirms = () => {
  const { data: firms } = useQuery({
    queryKey: firmKeys.all,
    queryFn: () => getAdminFirms(),
  });

  return firms;
};

export const useFirm = (firmId: string | undefined) => {
  const queryClient = useQueryClient();
  const { data: firm } = useQuery({
    queryKey: firmKeys.details(firmId ?? ''),
    queryFn: () => getAdminFirms({ firmId }),
    enabled: !!firmId,
    initialData: () => {
      const allFirms = queryClient.getQueryData<FirmInfo[]>(firmKeys.all);
      const firm = allFirms?.find((firm) => firm.id === firmId);
      return firm ? [firm] : undefined;
    },
  });

  return firm?.[0];
};

/**
 * Fetch matters from Firestore (lightweight, fast - for table display)
 * Does NOT include Finix payment instrument data
 */
export const useFirmMatters = (firmId: string | undefined) => {
  const { data: matters, isLoading } = useQuery({
    queryKey: firmKeys.mattersFirestore(firmId ?? ''),
    queryFn: async () => {
      if (!firmId) return [];

      const mattersRef = collection(
        firebase.firestore,
        `firms/${firmId}/matters`,
      );
      const q = query(mattersRef, orderBy('createdTimestamp', 'desc'));
      const snapshot = await getDocs(q);

      const matters: MatterDocument[] = snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Convert Firestore Timestamps to Date objects
          createdTimestamp:
            data.createdTimestamp?.toDate?.() || data.createdTimestamp,
        } as MatterDocument;
      });

      return matters.map(convertFirestoreMatter);
    },
    enabled: !!firmId,
  });

  return { matters: matters ?? [], isLoading };
};

/**
 * Fetch a single matter with full details including Finix payment instruments
 * Uses cloud function - slower but includes all data
 */
export const useFirmMatterDetail = (
  firmId: string | undefined,
  matterId: string | undefined,
) => {
  const { data: matter, isLoading } = useQuery({
    queryKey: firmKeys.matterWithFinix(firmId ?? '', matterId ?? ''),
    queryFn: async () => {
      if (!firmId || !matterId) return undefined;

      const mattersData = await getAdminMatters({
        firmId,
        matterId,
        includePaymentInstruments: true,
        finixEnvironment: shouldUseEmulators ? 'sandbox' : 'live',
      });

      return mattersData[0]
        ? deserializeMatterDetail(mattersData[0])
        : undefined;
    },
    enabled: !!firmId && !!matterId,
  });

  return { matter, isLoading };
};

export { type TransactionDocument };

/**
 * Fetch Firestore transactions for a specific matter
 */
export const useMatterTransactions = (
  firmId: string | undefined,
  matterId: string | undefined,
) => {
  const { data: transactions, isLoading } = useQuery({
    queryKey: firmKeys.matterTransactions(firmId ?? '', matterId ?? ''),
    queryFn: async () => {
      if (!firmId || !matterId) return [];

      const transactionsRef = collection(
        firebase.firestore,
        `firms/${firmId}/transactions`,
      );
      const q = query(transactionsRef, where('matter', '==', matterId));
      const snapshot = await getDocs(q);

      const transactions = snapshot.docs.map((doc) => {
        const data = doc.data() as TransactionData;
        return {
          ...data,
          id: doc.id,
          createdTimestamp:
            (
              data.createdTimestamp as unknown as { toDate?: () => Date }
            )?.toDate?.() || data.createdTimestamp,
          completedTimestamp:
            (
              data.completedTimestamp as unknown as { toDate?: () => Date }
            )?.toDate?.() || data.completedTimestamp,
        } as TransactionDocument;
      });

      return transactions.sort((a, b) => {
        const aTime = a.createdTimestamp
          ? new Date(a.createdTimestamp).getTime()
          : 0;
        const bTime = b.createdTimestamp
          ? new Date(b.createdTimestamp).getTime()
          : 0;
        return bTime - aTime;
      });
    },
    enabled: !!firmId && !!matterId,
  });

  return { transactions: transactions ?? [], isLoading };
};
